import { useCallback, useEffect, useState } from 'react';

import { safeGet, safeSet, safeCreateStore, safeDelete } from '@canvas/idb-cache';
import {
  CustomerImpact,
  CapabilityTier,
  EventClassification,
  Actionability,
} from '@canvas/operational-types';
import { RecoverableNetworkError } from '@canvas/retry';
import { captureExceptionForCapability } from '@canvas/sentry-react';

import { getPOPlacementProps } from './api.ts';
import {
  type FeatureName,
  PLACEMENT_ID,
  messages as messageConfigs,
} from './messages.ts';
import { useChangeboardingFilter } from './provider.tsx';
import { getChangeboardingOverrideFeature } from './query.ts';
import { messagesSeenState } from './state.ts';

export type MessageProps = {
  messageInstanceId: string;
  messageTemplateId: string;
  eventTime: string;
  createdAt: string;
  messageCategory: string;
  feature: string;
  messageCreationType?: string;
  messageTriggerId?: string;
  placementId?: string;
};

// Store that maps between features and their message instance IDs.
const FEATURE_INSTANCE_CACHE_DB = 'ct-changeboarding-feature-instance-db';
const FEATURE_INSTANCE_CACHE_STORE = 'ct-changeboarding-feature-instance-store';
const featureInstanceStore = safeCreateStore(
  FEATURE_INSTANCE_CACHE_DB,
  FEATURE_INSTANCE_CACHE_STORE,
);

// Store to cache the response from Post Office of active messages.
const MESSAGES_CACHE_DB = 'ct-changeboarding-messages-db';
const MESSAGES_CACHE_STORE = 'ct-changeboarding-messages-store';
const messagesStore = safeCreateStore(MESSAGES_CACHE_DB, MESSAGES_CACHE_STORE);

/**
 * Clears the cache for the messages. We don't need to clear the feature
 * instance cache because the instance ID of a feature never changes.
 */
export const clearCache = async () => {
  await safeDelete(MESSAGES_CACHE_STORE, messagesStore);
  await safeDelete(FEATURE_INSTANCE_CACHE_STORE, featureInstanceStore);
};

/**
 * Module-level promise that tracks an early cache clear triggered at app
 * startup. Any subsequent cache read (via `fetchAndSetMessages`) will await
 * this promise first, ensuring the stale cache is fully removed before new
 * data is fetched — even if React components mount before the async
 * IndexedDB delete completes.
 */
let earlyCacheClearPromise: Promise<void> | null = null;

/**
 * Kick off a cache clear as early as possible during app init. This is
 * intentionally safe to call from synchronous code — the returned promise
 * is stored internally and awaited by `fetchAndSetMessages` before any
 * cache reads occur.
 *
 * Calling this more than once is a no-op; the first invocation wins.
 */
export const requestEarlyCacheClear = (): void => {
  if (earlyCacheClearPromise === null) {
    earlyCacheClearPromise = clearCache().catch((e) => {
      // Gracefully swallow — the subsequent `initialiseChangeboardingMessages`
      // call will also clear the cache, so this is best-effort.
      captureExceptionForCapability(
        e,
        {
          tags: {
            captureLocation: 'requestEarlyCacheClear',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        [
          {
            capability: {
              importance: CapabilityTier.C3,
              kebabCaseName: 'changeboarding',
            },
          },
        ],
      );
    });
  }
};

/**
 * Wait for any in-flight early cache clear to finish. Safe to call even if
 * `requestEarlyCacheClear` was never invoked (resolves immediately).
 */
const waitForEarlyCacheClear = async (): Promise<void> => {
  if (earlyCacheClearPromise) {
    await earlyCacheClearPromise;
  }
};

/**
 * To initialise changeboarding messages, we need a forceful & fresh fetch and
 * set operation. To simplify this, we can just delete the store entries and
 * then fetch and set the necessary data.
 */
export const initialiseChangeboardingMessages = async () => {
  await clearCache();
  await fetchAndSetMessages();
};

const fetchAndSetMessages = async (): Promise<MessageProps[]> => {
  // Ensure any early cache clear (triggered at app startup) has completed
  // before we attempt to read from the cache. This closes the race condition
  // where a React component mounts and triggers a fetch before the async
  // IndexedDB delete has finished.
  await waitForEarlyCacheClear();

  // At this time, the RML component does not give us a way to retrieve
  // context as an object that can then be passed into our custom placement
  // component that we get from the RML library. That is, we get it in the
  // post office provider but this is a parent and separate to the child
  // placement component. So, we fetch it here again.

  // Other than retries for reliability, we're also caching the placement
  // props with an expiry of a day because we don't expect changes to occur
  // that often.
  try {
    const cachedData = await safeGet<{ value: MessageProps[]; expiresAt: number }>(
      MESSAGES_CACHE_STORE,
      {
        fallback: undefined,
        customStore: messagesStore,
      },
    );

    if (cachedData && cachedData.expiresAt > Date.now()) {
      return cachedData.value;
    }
  } catch (e) {
    captureExceptionForCapability(
      e,
      {
        tags: {
          captureLocation: 'fetchMessages-cache-get',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      },
      [
        {
          capability: {
            importance: CapabilityTier.C3,
            kebabCaseName: 'changeboarding',
          },
        },
      ],
    );
  }

  const data: { messages: MessageProps[] } = await getPOPlacementProps();

  // Filter out messages whose feature has been killswitched, so they don't
  // occupy a slot and block non-killswitched messages from being shown.
  // Also filter out messages already seen in the current session.
  const filteredMessages = data.messages.filter((message) => {
    const featureConfig = messageConfigs[message.feature as FeatureName] as
      | (typeof messageConfigs)[FeatureName]
      | undefined;
    if (featureConfig?.killswitch) {
      return false;
    }

    return (
      (message.feature in messagesSeenState && !messagesSeenState[message.feature]) ||
      !(message.feature in messagesSeenState)
    );
  });

  // Cache feature to instanceId mapping for quick lookup
  const featureToInstanceMap: Record<string, string> = {};
  filteredMessages.forEach((message) => {
    featureToInstanceMap[message.feature] = message.messageInstanceId;
  });

  await safeSet(
    FEATURE_INSTANCE_CACHE_STORE,
    {
      value: featureToInstanceMap,
      expiresAt: Date.now() + HOUR_EXPIRY,
    },
    featureInstanceStore,
  );

  await safeSet(
    MESSAGES_CACHE_STORE,
    {
      value: filteredMessages,
      expiresAt: Date.now() + HOUR_EXPIRY,
    },
    messagesStore,
  );

  return filteredMessages;
};

const HOUR_EXPIRY = 1000 * 60 * 60;

const generateMockMessage = (feature: string): MessageProps => {
  return {
    messageInstanceId: `mock-ct-changeboarding--${feature}`,
    // DO NOT CHANGE
    messageTemplateId: 'confluence-whiteboards-changeboarding-experiences',
    eventTime: Date.now().toString(),
    createdAt: Date.now().toString(),
    messageCategory: 'engagement',
    messageCreationType: 'implicit',
    messageTriggerId: 'mock-ct-changeboarding--trigger',
    placementId: PLACEMENT_ID,
    feature,
  };
};

/**
 * Hook to get changeboarding message data for a specific feature.
 *
 * Messages are filtered by product (whiteboards, databases, etc.) via a
 * context-provided filter from `PostOfficeProvider`. The filter defaults to
 * whiteboards when no product is explicitly set. After filtering, only the
 * first message in the resulting subset is considered — if its feature key
 * matches the requested `feature`, it is returned; otherwise the hook yields
 * no message (ensuring only the highest-priority changeboarding experience
 * for the current product is shown at a time).
 */
export const useChangeboardingMessage = (
  feature: string,
  isMessageSeen: boolean,
  skip = false,
) => {
  const productFilter = useChangeboardingFilter();

  const [messageData, setMessageData] = useState<MessageProps | undefined>(undefined);
  const [instanceId, setInstanceId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(!skip);

  const fetchMessageForFeature = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all messages and cache them - this also caches the feature-to-instance mapping
      const messages = await fetchAndSetMessages();

      // Narrow to the current product's subset, then only consider the
      // first (highest-priority) message for this product.
      const filteredMessages = productFilter(messages);
      const active = filteredMessages[0];
      let message: MessageProps | undefined =
        active?.feature === feature ? active : undefined;

      if (!message && getChangeboardingOverrideFeature() === feature) {
        message = generateMockMessage(feature);
      }

      setMessageData(message);
      if (message) {
        setInstanceId(message.messageInstanceId);
      }
    } catch (e) {
      if (e instanceof RecoverableNetworkError) {
        captureExceptionForCapability(
          e,
          {
            tags: {
              captureLocation: 'useChangeboardingMessage-fetchMessageForFeature',
              customerImpact: CustomerImpact.CUSTOMERS,
              eventClassification: EventClassification.LOG,
              actionability: Actionability.NONE,
            },
          },
          [
            {
              capability: {
                importance: CapabilityTier.C3,
                kebabCaseName: 'changeboarding',
              },
            },
          ],
        );
      } else {
        captureExceptionForCapability(
          e,
          {
            tags: {
              captureLocation: 'useChangeboardingMessage-fetchMessageForFeature',
              customerImpact: CustomerImpact.CUSTOMERS,
              eventClassification: EventClassification.UNKNOWN,
              actionability: Actionability.UNKNOWN,
            },
          },
          [
            {
              capability: {
                importance: CapabilityTier.C3,
                kebabCaseName: 'changeboarding',
              },
            },
          ],
        );
      }
    } finally {
      setLoading(false);
      // This is a query-param override to get the changeboarding message to
      // show for the requested feature, if it exists in our config.
      if (getChangeboardingOverrideFeature() === feature) {
        const message = generateMockMessage(feature);
        setInstanceId(message.messageInstanceId);
        setMessageData(message);
      }
    }
  }, [feature, productFilter]);

  useEffect(() => {
    if (skip || isMessageSeen) {
      if (skip) {
        setLoading(false);
        setMessageData(undefined);
        setInstanceId(undefined);
      }
      return;
    }

    fetchMessageForFeature();
  }, [fetchMessageForFeature, isMessageSeen, skip]);

  // Speed up the process - don't fetch the message if it's already been seen in
  // this active session or if we're skipping.
  if (skip || isMessageSeen) {
    return {
      messageData: undefined,
      instanceId: undefined,
      loading: false,
    } as const;
  }

  return {
    messageData,
    instanceId,
    loading,
    // Return the specific message data for the feature in question rather than
    // the first entry, or all messages.
    placementProps: messageData,
  } as const;
};
